import { Helmet } from 'react-helmet-async';
import { useEffect, useState } from 'react';
import { Box, Button, Container, Stack, Typography } from '@mui/material';
import Iconify from 'src/components/iconify/index.tsx';
import { useAppSearchParams } from '@/hooks/useAppSearchParams.ts';
import { useNavigate } from 'react-router-dom';

import { api } from '@/api/api-client.config.ts';
import AssetsDialog from '@/components/pickem-admin/AssetsDialog.tsx';
import { Pickem } from '@/@types/api.types.ts';
import { useGetPickem } from '@/api/pickems/getPickem.ts';
import { usePickemStore } from '@/store/usePickem.store.ts';
import { visibleLanguages } from '@/config/quill-text-editor-settings.ts';
import { EditGeneralSettings } from '@/components/pickem-edit/EditGeneralSettings.tsx';
import { usePickemForm } from '@/store/usePickemForm.context.ts';
import { LanguageTabs, TabPanel } from '@/components/language-tabs/index.tsx';
import { EditTextSettings } from '@/components/pickem-edit/EditTextSettings.tsx';
import { PickemRulesEdit } from '@/components/pickem-admin/PickemRulesEdit.tsx';
import { EditPrizeSettings } from '@/components/pickem-edit/EditPrizeSettings.tsx';
import { PickemLeaderboardSection } from '@/components/pickem-edit/PickemLeaderboardSection/index.tsx';
import { QuestionsSection } from '@/components/pickem-edit/QuestionsSection/index.tsx';
import { PickemTypeEnum } from '@/enums/Pickem.enums';
import { ParticipantsSection } from '../../components/pickem-edit/ParticipantsSection';

const getStatsMatchIds = (pickem: Pickem) => {
  let matchIds = [];
  for (let i = 0; i < pickem.questions.length; i++) {
    let question = pickem.questions[i];
    for (let j = 0; j < question.options.length; j++) {
      let option = question.options[j];
      if (option?.data?.match_id) {
        matchIds.push(option.data.match_id);
      }
    }
  }
  return matchIds;
};

export default function PickemEditPage() {
  const navigate = useNavigate();
  const [matchStatsIds, setMatchStatsIds] = useState<number[]>([]);
  const [matchStats, setMatchStats] = useState<any>(null);

  const [assetsTarget, setAssetsTarget] = useState<string | null>(null);

  const { pickemId } = useAppSearchParams();

  const { isScoringMode, setIsPrivateCompetition } = usePickemStore();

  const { data: pickem } = useGetPickem(pickemId);

  useEffect(() => {
    if (pickem) {
      setIsPrivateCompetition(pickem.pickem_type === PickemTypeEnum.PRIVATE);
    }
  }, [pickem]);

  const form = usePickemForm({
    defaultValues:
      pickem ??
      ({
        pickem_id: null,
        title_en: '',
        title_fr: '',
        title_sl: '',
        title_es: '',
        title_lt: '',
        title_rus: '',
        title_de: '',
        title_sp: '',
        title_ca: '',
        description_en: '',
        description_fr: '',
        description_sl: '',
        description_es: '',
        description_lt: '',
        description_rus: '',
        description_de: '',
        description_sp: '',
        description_ca: '',
        start: '',
        allow_selection_start: null,
        tenants: [],
        gamezone_image_en: '',
        gamezone_image_fr: '',
        gamezone_image_sl: '',
        gamezone_image_es: '',
        gamezone_image_lt: '',
        gamezone_image_rus: '',
        gamezone_image_de: '',
        gamezone_image_sp: '',
        gamezone_image_ca: '',
        text_gamezone_image_en: '',
        text_gamezone_image_fr: '',
        text_gamezone_image_sl: '',
        text_gamezone_image_es: '',
        text_gamezone_image_lt: '',
        text_gamezone_image_rus: '',
        text_gamezone_image_sp: '',
        text_gamezone_image_ca: '',
        status: 'DRAFT',
        pickem_type: PickemTypeEnum.PUBLIC,
        prize_text_en: '',
        prize_text_fr: '',
        prize_text_sl: '',
        prize_text_lt: '',
        prize_text_es: '',
        prize_text_rus: '',
        prize_text_de: '',
        prize_text_sp: '',
        prize_text_ca: '',
        prizes_desc_en: {},
        prizes_desc_fr: {},
        prizes_desc_sl: {},
        prizes_desc_lt: {},
        prizes_desc_es: {},
        prizes_desc_rus: {},
        prizes_desc_de: {},
        prizes_desc_sp: {},
        prizes_desc_ca: {},
        prize_image_en: '',
        prize_image_fr: '',
        prize_image_sl: '',
        prize_image_es: '',
        prize_image_lt: '',
        prize_image_rus: '',
        prize_image_de: '',
        prize_image_sp: '',
        prize_image_ca: '',
        text_prize_image_en: '',
        text_prize_image_fr: '',
        text_prize_image_sl: '',
        text_prize_image_es: '',
        text_prize_image_lt: '',
        text_prize_image_rus: '',
        text_prize_image_sp: '',
        text_prize_image_ca: '',
        main_image_en: '',
        main_image_fr: '',
        main_image_sl: '',
        main_image_es: '',
        main_image_lt: '',
        main_image_rus: '',
        main_image_de: '',
        main_image_sp: '',
        main_image_ca: '',
        text_main_image_en: '',
        text_main_image_fr: '',
        text_main_image_sl: '',
        text_main_image_es: '',
        text_main_image_lt: '',
        text_main_image_rus: '',
        text_main_image_sp: '',
        text_main_image_ca: '',
        prizesBreakdown: [],
        questions: [],
        tenants_prefixes: [],
      } as unknown as Pickem),
    onSubmit: async ({ value }) => {
      api
        .put(`/admin/pickems/${pickemId}`, value)
        .then(() => window.location.reload());
    },
  });

  useEffect(() => {
    if (pickem) {
      const matchIds = getStatsMatchIds(pickem);
      if (matchIds.length > 0) {
        setMatchStatsIds(matchIds);
      }
    }
  }, [pickem]);

  const fetchMatchStats = async () => {
    try {
      const requests = matchStatsIds.map((matchId) =>
        api.get<void, { match: any }>(`/admin/matches/${matchId}`)
      );

      const responses = await Promise.all(requests);
      const combinedMatchStats = responses.map((data) => data.match);
      setMatchStats(combinedMatchStats.flat());
    } catch (error) {
      console.error('Error fetching match stats:', error);
    }
  };

  useEffect(() => {
    if (matchStatsIds !== null) {
      fetchMatchStats();
    }
  }, [matchStatsIds]);

  const [value, setValue] = useState<number>(0);

  const handleChangeTabs = (_event: any, newValue: number) => {
    setValue(newValue);
  };

  const [mainTabValue, setMainTabValue] = useState<number>(0);

  const handleChangeMainTabs = (_event: any, newValue: number) => {
    setMainTabValue(newValue);
  };

  const bonusOptionClickHandler = (questionId: number, optionLabel: string) => {
    if (!isScoringMode) return;

    const questions = form.getFieldValue('questions');

    let questionIndex = questions.findIndex(
      (question) => question.question_id === questionId
    );
    if (questionIndex !== -1) {
      let optionIndex = questions[questionIndex].bonus_options.findIndex(
        (option) => option.label === optionLabel
      );

      if (optionIndex !== -1) {
        questions[questionIndex].bonus_options[optionIndex].is_correct = !(
          questions[questionIndex].bonus_options[optionIndex]?.is_correct &&
          questions[questionIndex].bonus_options[optionIndex].is_correct
        );

        form.setFieldValue('questions', questions);
      }
    }
  };

  const onChoseAsset = (assetUrl: string) => {
    if (!assetsTarget) return;

    form.setFieldValue(assetsTarget as keyof Pickem, assetUrl);
    setAssetsTarget(null);
  };

  const onCloseAssetsDialog = () => {
    setAssetsTarget(null);
  };

  return (
    <>
      <Helmet>
        <title> Pickem Edit </title>
      </Helmet>

      <Container>
        <form.AppForm>
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              await form.handleSubmit();
            }}
          >
            <Stack
              direction='row'
              alignItems='center'
              justifyContent='space-between'
              mb={5}
            >
              <Typography variant='h4' gutterBottom>
                Pickem Edit
              </Typography>

              <Box>
                <Button
                  variant='outlined'
                  startIcon={<Iconify icon='eva:close-outline' />}
                  onClick={() => navigate(`/dashboard/pickems`)}
                  sx={{ mr: 2 }}
                >
                  Cancel
                </Button>
                <Button
                  type='submit'
                  variant='contained'
                  startIcon={<Iconify icon='eva:save-outline' />}
                >
                  Save
                </Button>
              </Box>
            </Stack>

            <Box sx={{ width: '100%' }}>
              <form.MainTabs
                value={mainTabValue}
                onChange={handleChangeMainTabs}
              />

              {/* General Settings tab */}
              <TabPanel value={mainTabValue} index={0}>
                <EditGeneralSettings form={form} />
                <Box sx={{ width: '100%' }}>
                  <LanguageTabs value={value} onChange={handleChangeTabs} />
                  {visibleLanguages.map((lang, index) => (
                    <TabPanel key={lang.code} value={value} index={index}>
                      <EditTextSettings
                        form={form}
                        lang={lang.code}
                        setAssetsTarget={setAssetsTarget}
                      />
                    </TabPanel>
                  ))}
                </Box>
              </TabPanel>

              {/* Questions tab */}
              <TabPanel value={mainTabValue} index={1}>
                <Box sx={{ width: '100%' }}>
                  <LanguageTabs value={value} onChange={handleChangeTabs} />

                  {visibleLanguages.map((lang, index) => (
                    <TabPanel key={lang.code} value={value} index={index}>
                      <QuestionsSection
                        form={form}
                        lang={lang}
                        bonusOptionClickHandler={bonusOptionClickHandler}
                        matchStats={matchStats}
                      />
                    </TabPanel>
                  ))}
                </Box>
              </TabPanel>

              {/*/!* Prizing tab *!/*/}
              <TabPanel value={mainTabValue} index={2}>
                <EditPrizeSettings form={form} />
              </TabPanel>

              {/* Leaderboard tab */}
              <TabPanel value={mainTabValue} index={3}>
                <PickemLeaderboardSection />
              </TabPanel>

              {/* Rules tab */}
              <TabPanel value={mainTabValue} index={4}>
                <PickemRulesEdit />
              </TabPanel>

              {/* Participants tab */}
              <TabPanel value={mainTabValue} index={5}>
                <ParticipantsSection />
              </TabPanel>
            </Box>
          </form>
        </form.AppForm>
      </Container>

      {assetsTarget && (
        <AssetsDialog onSuccess={onChoseAsset} onClose={onCloseAssetsDialog} />
      )}
    </>
  );
}
